import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, IconButton,
  Tooltip, Chip, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, FormControlLabel, Checkbox,
  Switch, Grid, InputAdornment, Divider, Container, Stack
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ClearIcon from '@mui/icons-material/Clear';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';

import Collapse from '@mui/material/Collapse';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';

const TAX_REGIMES = {
  '48': '48 - Responsable de IVA',
  '49': '49 - No responsable de IVA',
  'SIMPLE': 'SIMPLE - Régimen Simple',
};

const OVERRIDABLE_FISCAL_FIELDS = new Set([
  'is_self_withholding',
  'applies_ica_withholding',
  'tax_regime',
  'is_gran_contribuyente',
  'applies_vat_withholding',
  'applies_source_withholding',
]);

const ThirdPartyConfig = ({ thirdPartyStore }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [data, setData] = useState({
    thirdParties: [],
    totalCount: 0,
    isLoading: true,
    error: null
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [editModal, setEditModal] = useState({ open: false, thirdParty: null, dirtyFiscalFields: [] });
  const [saving, setSaving] = useState(false);

  const searchTimeout = useRef(null);

  useEffect(() => {
    if (!thirdPartyStore) return;

    const unsubscribe = thirdPartyStore.subscribe((state) => {
      setData({
        thirdParties: state.thirdParties,
        totalCount: state.totalCount,
        isLoading: state.isLoading,
        error: state.error
      });
    });

    thirdPartyStore.getState().fetchThirdParties(page, rowsPerPage, search);

    return () => unsubscribe();
  }, [thirdPartyStore, page, rowsPerPage, search]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    
    searchTimeout.current = setTimeout(() => {
      setSearch(value);
      setPage(0); // Reset page on search
    }, 500);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditClick = (tp) => {
    setEditModal({ open: true, thirdParty: { ...tp }, dirtyFiscalFields: [] });
  };

  const handleCloseModal = () => {
    setEditModal({ open: false, thirdParty: null, dirtyFiscalFields: [] });
  };

  const handleFieldChange = (field, value) => {
    setEditModal(prev => ({
      ...prev,
      thirdParty: { ...prev.thirdParty, [field]: value },
      dirtyFiscalFields: OVERRIDABLE_FISCAL_FIELDS.has(field)
        ? [...new Set([...(prev.dirtyFiscalFields || []), field])]
        : prev.dirtyFiscalFields
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    const { thirdParty } = editModal;
    const updateData = {
      dian_fiscal_responsibility: thirdParty.dian_fiscal_responsibility,
      is_dian_responsibility_locked: thirdParty.is_dian_responsibility_locked,
      default_expense_account: thirdParty.default_expense_account
    };
    (editModal.dirtyFiscalFields || []).forEach((field) => {
      updateData[field] = thirdParty[field];
    });
    const result = await thirdPartyStore.getState().updateThirdParty(thirdParty.id_n, updateData);
    setSaving(false);
    if (result.success) {
      handleCloseModal();
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'SYNCED':
        return <Chip size="small" icon={<CheckCircleIcon />} label="Sincronizado" color="success" variant="outlined" />;
      case 'PENDING':
        return <Chip size="small" icon={<SyncIcon className="animate-spin" />} label="Pendiente Sync" color="warning" variant="outlined" />;
      case 'ERROR':
        return <Chip size="small" icon={<SyncProblemIcon />} label="Error" color="error" variant="outlined" />;
      default:
        return <Chip size="small" label={status || 'Desconocido'} variant="outlined" />;
    }
  };

  if (data.error) return <Alert severity="error">{data.error}</Alert>;

  const handleToggleSelect = (id_n) => {
    setSelectedIds(prev => 
      prev.includes(id_n) ? prev.filter(id => id !== id_n) : [...prev, id_n]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = data.thirdParties.map(tp => tp.id_n);
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleBulkAction = async (updateData) => {
    if (selectedIds.length === 0) return;
    
    const result = await thirdPartyStore.getState().bulkUpdateThirdParties(selectedIds, updateData);
    if (result.success) {
      setSelectedIds([]);
      // we don't need to re-fetch as the store updates local state
    } else {
      alert(`Error en actualización masiva: ${result.error}`);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 3 }}>
        Gestión de Terceros y Configuración ERP
      </Typography>

      {/* Barra de Acciones Masivas */}
      <Collapse in={selectedIds.length > 0}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 2, 
            mb: 2, 
            display: 'flex', 
            alignItems: 'center', 
            bgcolor: 'primary.light',
            color: 'white',
            borderRadius: 2
          }}
        >
          <Typography variant="subtitle1" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            {selectedIds.length} terceros seleccionados
          </Typography>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Fijar Régimen 48">
              <Button 
                variant="contained" 
                size="small" 
                color="secondary"
                onClick={() => handleBulkAction({ tax_regime: '48' })}
              >
                R. 48
              </Button>
            </Tooltip>
            <Tooltip title="Fijar Régimen 49">
              <Button 
                variant="contained" 
                size="small" 
                color="secondary"
                onClick={() => handleBulkAction({ tax_regime: '49' })}
              >
                R. 49
              </Button>
            </Tooltip>
            <Tooltip title="Activar Autorretención">
              <Button 
                variant="contained" 
                size="small" 
                sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: '#f0f0f0' } }}
                onClick={() => handleBulkAction({ is_self_withholding: true })}
              >
                +Autoret
              </Button>
            </Tooltip>
            <Tooltip title="Quitar Autorretención">
              <Button 
                variant="outlined" 
                size="small" 
                sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: '#f0f0f0', bgcolor: 'rgba(255,255,255,0.1)' } }}
                onClick={() => handleBulkAction({ is_self_withholding: false })}
              >
                -Autoret
              </Button>
            </Tooltip>
            <Tooltip title="Activar ReteFuente">
              <Button 
                variant="contained" 
                size="small" 
                sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: '#f0f0f0' } }}
                onClick={() => handleBulkAction({ applies_source_withholding: true })}
              >
                +RFuente
              </Button>
            </Tooltip>
            <Tooltip title="Quitar ReteFuente">
              <Button 
                variant="outlined" 
                size="small" 
                sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: '#f0f0f0', bgcolor: 'rgba(255,255,255,0.1)' } }}
                onClick={() => handleBulkAction({ applies_source_withholding: false })}
              >
                -RFuente
              </Button>
            </Tooltip>
            {/* ICA */}
            <Tooltip title="Activar ReteICA">
              <Button 
                variant="contained" 
                size="small" 
                sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: '#f0f0f0' } }}
                onClick={() => handleBulkAction({ applies_ica_withholding: true })}
              >
                +ICA
              </Button>
            </Tooltip>
            <Tooltip title="Quitar ReteICA">
              <Button 
                variant="outlined" 
                size="small" 
                sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: '#f0f0f0', bgcolor: 'rgba(255,255,255,0.1)' } }}
                onClick={() => handleBulkAction({ applies_ica_withholding: false })}
              >
                -ICA
              </Button>
            </Tooltip>
            {/* IVA */}
            <Tooltip title="Activar ReteIVA">
              <Button 
                variant="contained" 
                size="small" 
                sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: '#f0f0f0' } }}
                onClick={() => handleBulkAction({ applies_vat_withholding: true })}
              >
                +IVA
              </Button>
            </Tooltip>
            <Tooltip title="Quitar ReteIVA">
              <Button 
                variant="outlined" 
                size="small" 
                sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: '#f0f0f0', bgcolor: 'rgba(255,255,255,0.1)' } }}
                onClick={() => handleBulkAction({ applies_vat_withholding: false })}
              >
                -IVA
              </Button>
            </Tooltip>
            <Button 
              size="small" 
              color="inherit" 
              onClick={() => setSelectedIds([])}
            >
              Cancelar
            </Button>
          </Stack>
        </Paper>
      </Collapse>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar por NIT o Razón Social..."
          defaultValue={search}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        {data.isLoading && <CircularProgress size={24} />}
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
            <TableHead sx={{ bgcolor: 'grey.100' }}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedIds.length > 0 && selectedIds.length < data.thirdParties.length}
                    checked={data.thirdParties.length > 0 && selectedIds.length === data.thirdParties.length}
                    onChange={handleSelectAll}
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Identificación (NIT)</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Razón Social</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Régimen</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Autorret.</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>ReteICA</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>G.Contrib.</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Estado Sync</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.thirdParties.length === 0 && !data.isLoading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                  No se encontraron terceros.
                </TableCell>
              </TableRow>
            ) : (
              data.thirdParties.map((tp) => (
                <TableRow 
                  key={tp.id_n} 
                  hover 
                  selected={selectedIds.includes(tp.id_n)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.includes(tp.id_n)}
                      onChange={() => handleToggleSelect(tp.id_n)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{tp.nit || tp.id_n}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>
                    {tp.company && tp.company !== 'SIN NOMBRE' 
                      ? tp.company 
                      : (tp.company_short && tp.company_short !== 'SIN NOMBRE' 
                          ? tp.company_short 
                          : (tp.nit || tp.id_n || 'SIN NOMBRE'))}
                  </TableCell>
                  <TableCell>{TAX_REGIMES[tp.tax_regime] || tp.tax_regime || 'N/A'}</TableCell>
                  <TableCell align="center">
                    <Checkbox checked={!!tp.is_self_withholding} disabled size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox checked={!!tp.applies_ica_withholding} disabled size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox checked={!!tp.is_gran_contribuyente} disabled size="small" />
                  </TableCell>
                  <TableCell align="center">
                    {getStatusChip(tp.sync_status)}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar configuración fiscal">
                      <IconButton size="small" onClick={() => handleEditClick(tp)} color="primary">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={data.totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="Filas por página"
        />
      </TableContainer>

      {/* Modal de Edición */}
      <Dialog open={editModal.open} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', borderBottom: 1, borderColor: 'divider' }}>
          Configuración Fiscal: {
            editModal.thirdParty?.company && editModal.thirdParty?.company !== 'SIN NOMBRE'
              ? editModal.thirdParty?.company
              : (editModal.thirdParty?.company_short && editModal.thirdParty?.company_short !== 'SIN NOMBRE'
                  ? editModal.thirdParty?.company_short
                  : (editModal.thirdParty?.nit || editModal.thirdParty?.id_n || 'SIN NOMBRE'))
          }
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Los cambios fiscales se guardan como excepciones del sistema. La información
                recibida del ERP se conserva por separado y no se sobrescribe.
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Responsabilidad Fiscal (DIAN)"
                placeholder="Ej: O-13;O-15"
                value={editModal.thirdParty?.dian_fiscal_responsibility || ''}
                onChange={(e) => {
                  handleFieldChange('dian_fiscal_responsibility', e.target.value);
                  // Bloquear automáticamente si el usuario lo edita manualmente
                  handleFieldChange('is_dian_responsibility_locked', true);
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Cuenta de Gasto por Defecto"
                placeholder="Ej: 52956001"
                value={editModal.thirdParty?.default_expense_account || ''}
                onChange={(e) => handleFieldChange('default_expense_account', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!editModal.thirdParty?.is_dian_responsibility_locked}
                    onChange={(e) => handleFieldChange('is_dian_responsibility_locked', e.target.checked)}
                    color="warning"
                  />
                }
                label="Bloquear act. automática"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={handleCloseModal} color="inherit">Cancelar</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            {saving ? 'Guardando...' : 'Guardar excepción'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .animate-spin {
            animation: spin 2s linear infinite;
          }
        `}
      </style>
      </Container>
    );
  };

export default ThirdPartyConfig;
